/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Match boosting", description = "Increase match rates for your audiences in advertising platforms.", lastUpdatedDate = new Date(1695254400000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Match boosting",
        description: "Increase match rates for your audiences in advertising platforms.",
        lastUpdatedDate: "Thu Sep 21 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Implementation steps",
          "depth": 0,
          "id": "implementation-steps"
        }, {
          "value": "Sync configuration",
          "depth": 1,
          "id": "sync-configuration"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      p: "p",
      strong: "strong",
      a: "a",
      h2: "h2",
      ol: "ol",
      li: "li",
      h3: "h3",
      img: "img"
    }, _provideComponents(), props.components), {BtOnly} = _components;
    if (!BtOnly) _missingMdxReference("BtOnly", true);
    return _jsxs(_Fragment, {
      children: [_jsx(BtOnly, {
        featureName: "Match Booster",
        extraText: ". It can be used with or without Customer Studio."
      }), "\n", _jsxs(_components.p, {
        children: ["Match Booster onboards your 1st party data and improves ", _jsx(_components.strong, {
          children: "match rates"
        }), " on ", _jsx(_components.a, {
          href: "/match-booster/overview#supported-destinations",
          children: "various paid advertising platforms"
        }), " by providing additional user identifiers. Learn more in the main ", _jsx(_components.a, {
          href: "/match-booster/overview",
          children: "Match Booster documentation"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "implementation-steps",
        children: "Implementation steps"
      }), "\n", _jsx(_components.p, {
        children: "Using Match Booster is a two-step process:"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsx(_components.li, {
          children: "Enrichment: configure Match Booster to enrich your models in transit"
        }), "\n", _jsxs(_components.li, {
          children: ["Sync configuration: select which enriched identifiers to send into ", _jsx(_components.a, {
            href: "/match-booster/overview#supported-destinations",
            children: "supported destinations"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've enabled ", _jsx(_components.a, {
          href: "/match-booster/implementation#model-configuration",
          children: "match boosting on a model"
        }), ", you can configure match boosting on syncs based on the model.\nThese include ", _jsx(_components.a, {
          href: "/match-booster/implementation#sync-configuration",
          children: "audience syncs"
        }), " in Customer Studio."]
      }), "\n", _jsx(_components.p, {
        children: "The following instructions show you how to implement match boosting on existing syncs, but they also apply when configuring syncs for the first time."
      }), "\n", _jsx(Info, {
        children: _jsxs(_components.p, {
          children: ["You can only configure Match Booster on syncs to ", _jsx(_components.a, {
            href: "/match-booster/overview#supported-destinations",
            children: "supported\ndestinations"
          }), "."]
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to your ", _jsxs(_components.a, {
            href: "https://app.hightouch.com/syncs",
            children: [_jsx(_components.strong, {
              children: "Syncs"
            }), " overview"]
          }), " page and select the sync on which you want to configure ", _jsx(_components.strong, {
            children: "Match Booster"
          }), ". Alternatively, create a new sync."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Configuration"
          }), " tab, ensure your ", _jsx(_components.a, {
            href: "/syncs/types-and-modes#sync-types",
            children: "sync type"
          }), " is set to custom audience, customer match list, or segment. Match Booster only applies to ", _jsx(_components.a, {
            href: "/match-booster/implementation#sync-configuration",
            children: "customer segment sync types"
          }), "—not events or other objects."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/match-boosting-sync-type.png",
          alt: "Match Boosting in syncs"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Locate the field mapping section with the text ", _jsx(_components.strong, {
            children: "Match boosting enabled"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/match-boosting.png",
          alt: "Match Boosting in syncs"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "boosted column names"
          }), " you want to map to fields in your destination."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "syncs/boosted-column-mapping.png",
          alt: "Match Boosting in syncs"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Save"
          }), " your sync configuration."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["From now on, Hightouch will sync any available additional identifiers to your destination.\nModel enrichment can take up to few hours to process.\nTherefore, if you enabled match boosting in your ", _jsx(_components.a, {
          href: "/match-booster/implementation#model-configuration",
          children: "model configuration"
        }), " shortly before updating or creating your sync configuration,\nit may take a few hours for you to see ", _jsx(_components.a, {
          href: "/match-booster/implementation#view-match-rates",
          children: "increased match rates"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: "Additionally, ad platforms have different practices on how frequently they calculate and update match rates, so you may continue see variation for a day or two after updating your configuration."
      }), "\n", _jsx(Tip, {
        children: _jsxs(_components.p, {
          children: ["Check out the main ", _jsx(_components.a, {
            href: "/match-booster/faqs",
            children: "Match Booster documentation"
          }), " page for\nFAQs."]
        })
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
